import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { ICONS } from "src/app/_helpers/icons.constants";
import { ButtonComponent } from "src/app/shared/design-system/button/button.component";
import { ButtonConfig } from "src/app/shared/design-system/button/button.modal";
import { PanelComponent } from "src/app/shared/design-system/panel/panel.component";
import { PanelConfig } from "src/app/shared/design-system/panel/panel.modal";
import { RadioButtonComponent } from "src/app/shared/design-system/radio-button/radio-button.component";
import { RadioConfig } from "src/app/shared/design-system/radio-button/radio.modal";
import {
  SmartImportAnalysisResponse,
  SmartImportPageGroup,
  ClassifySmartImportStatementRequest,
  sortSmartImportPageGroups,
} from "src/app/_models/credit-card/smart-import";
import { SmartImportPageGroupActionEnum } from "src/app/_models/enum/solo-enum";
import { ApiRequest } from "src/app/_models/common/request-response/api-request";
import { environment } from "src/environments/environment";
import { SmartImportReaderComponent } from "../smart-import-reader/smart-import-reader.component";
import { SmartImportAcceptSuggestionsComponent } from "../smart-import-accept-suggestions/smart-import-accept-suggestions.component";

/** One of the three side-by-side classification boxes rendered by the wizard's `@for`. */
interface SmartImportActionCard {
  action: SmartImportPageGroupActionEnum;
  /** Coloured while this card is the group's decision, neutral white otherwise. */
  containerClass: string;
  aiSuggested: boolean;
  /** A one-option group — the shared `name` is what keeps the three cards mutually exclusive. */
  radio: RadioConfig;
}

@Component({
  selector: "smart-import-wizard",
  templateUrl: "./smart-import-wizard.component.html",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NgxExtendedPdfViewerModule,
    SmartImportReaderComponent,
    ButtonComponent,
    PanelComponent,
    RadioButtonComponent,
    SmartImportAcceptSuggestionsComponent,
  ],
})
export class SmartImportWizardComponent implements OnChanges {
  @Input() analysisResult: SmartImportAnalysisResponse;
  @Input() organisationName: string = "";
  @Input() submitting = false;
  @Input() submitError = false;
  @Output() cancelled = new EventEmitter<void>();
  @Output() submitClassification = new EventEmitter<{
    request: ApiRequest<ClassifySmartImportStatementRequest>;
    transactionCount: number;
  }>();

  public readonly ICONS = ICONS;
  public readonly Action = SmartImportPageGroupActionEnum;

  groups: SmartImportPageGroup[] = [];
  selectedGroupId: number | null = null;
  // True when at least one group arrived already classified — that only happens when its
  // fingerprint matched a rule remembered from an earlier statement of the same format, so
  // it doubles as "have we seen this template before" without needing a dedicated API field.
  isRecognisedFormat = false;
  // Which page WITHIN the selected group the mini-preview is currently showing (a group
  // can span several pages — spec US-06 chevrons cycle through them, they don't switch
  // to a different group).
  previewPageIndex = 0;
  readerOpen = false;
  readerPageIndex = 0;
  // ngx-extended-pdf-viewer keeps its state on a single global `window.PDFViewerApplication`
  // object, so this component's own mini-preview viewer and the reader's full-screen viewer
  // must never both be mounted at once — the second instance to init hijacks the global, and
  // tearing either one down then corrupts whichever viewer is left. This flag is toggled a
  // tick after the reader closes so the reader's viewer fully destroys first.
  miniPreviewVisible = true;
  previewLoading = true;

  /**
   * The bulk-accept CTA. NOT a getter: `ds-button` reads its input in `ngOnChanges`, so this has to
   * be a stable reference that is *replaced* only when the eligible count actually changes — a getter
   * returning a fresh object on every change-detection pass would re-run that hook each tick.
   * Kept in step by `syncAiAcceptButton()`, called from the three places `groups`/`action` change.
   */
  aiAcceptButtonConfig: ButtonConfig;

  /**
   * The three classification cards, in render order. `ds-radio-button` reads its input in
   * `ngOnChanges`, so — like `aiAcceptButtonConfig` — this array is replaced only by
   * `syncActionCards()` when the selected group or its action changes, never a getter.
   */
  actionCards: SmartImportActionCard[] = [];

  // The colour each card takes once it IS the group's decision — error for Never Import, orange
  // (the secondary-subtle tone Skip already carries in the group rail) for Skip, success for
  // Import. Undecided cards stay neutral white.
  private static readonly ACTION_OPTIONS: {
    action: SmartImportPageGroupActionEnum;
    labelKey: string;
    selectedContainerClass: string;
    radioColor: RadioConfig["color"];
  }[] = [
    {
      action: SmartImportPageGroupActionEnum.NeverImport,
      labelKey: "smart_import_never_import",
      selectedContainerClass: "error-info-container --borderless",
      radioColor: "danger",
    },
    {
      action: SmartImportPageGroupActionEnum.Skip,
      labelKey: "skip",
      selectedContainerClass: "orange-info-container --borderless",
      radioColor: "orange",
    },
    {
      action: SmartImportPageGroupActionEnum.Import,
      labelKey: "label_import",
      selectedContainerClass: "success-info-container --borderless",
      radioColor: "success",
    },
  ];

  constructor(private translate: TranslateService) {
    this.syncAiAcceptButton();
    this.syncActionCards();
  }

  /**
   * Rebuilds all three cards for whichever group is selected: the one matching the group's action
   * is checked and coloured, and the AI's recommendation shows as a `ds-info-text` line on the
   * card it recommends — the same `isAiSuggested` gate the old per-button badges used, so a line
   * appears for exactly the groups that used to show a badge and goes once the group is classified.
   */
  private syncActionCards(): void {
    const group = this.selectedGroup;
    // Shared across all three inputs, so the browser itself treats them as one radio group even
    // though each card is a separate `ds-radio-button` instance with its own single option.
    const groupName = "si-action-" + (group?.id ?? 0);
    this.actionCards = SmartImportWizardComponent.ACTION_OPTIONS.map((opt, index) => {
      const isSelected = !!group && group.action === opt.action;
      return {
        action: opt.action,
        containerClass: isSelected ? opt.selectedContainerClass : "white-info-container",
        aiSuggested: !!group && this.isAiSuggested(group, opt.action),
        radio: {
          // Unique per card — `ds-radio-button` derives its input and label ids from this, so a
          // shared id would point all three labels at the first card's input.
          id: groupName + "-" + index,
          type: "radio" as const,
          // Matches the card's own colour, so the dot and the box read as one state.
          color: opt.radioColor,
          size: "md" as const,
          options: [
            {
              name: groupName,
              value: opt.action,
              label: this.translate.instant(opt.labelKey),
              checked: isSelected,
            },
          ],
        },
      };
    });
  }

  onActionSelected(action: SmartImportPageGroupActionEnum): void {
    const group = this.selectedGroup;
    if (group) {
      this.classify(group, action);
    }
  }

  /** Empty state drops the icon and disables the button, exactly as the old markup's `@if` did. */
  private syncAiAcceptButton(): void {
    const count = this.eligibleForBulkAccept.length;
    const label =
      count === 0
        ? this.translate.instant("smart_import_no_high_confidence_left")
        : this.translate.instant("smart_import_accept_n_suggestions", { count });
    this.aiAcceptButtonConfig = {
      id: "smartImportAcceptAll",
      label,
      ariaLabel: label,
      icon: count === 0 ? "" : ICONS.GEMINI_AI,
      iconPosition: "left",
      color: "smart-ai",
      size: "md",
      disabled: count === 0,
    };
  }

  get panelConfig(): PanelConfig {
    return {
      title: this.translate.instant(
        this.isRecognisedFormat ? "smart_import_pdf_format_recognised" : "smart_import_new_pdf_format_detected"
      ),
      size: "xxl",
      position: "right",
      hasOverlay: true,
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.analysisResult && this.analysisResult) {
      const rawGroups = this.analysisResult.pageGroups || [];
      // Checked BEFORE the rememberRule remap below — a group only arrives pre-classified
      // (action already set) when its fingerprint matched a rule from an earlier statement.
      this.isRecognisedFormat = rawGroups.some((g) => g.action !== null && g.action !== undefined);

      this.groups = sortSmartImportPageGroups(rawGroups).map((g) => ({
        ...g,
        // Always starts checked regardless of what the backend sent (it currently sends an
        // explicit false at analysis time, not null/undefined, so a `?? true` fallback alone
        // wouldn't do it) — the FM opts out per group, rather than opting in.
        rememberRule: true,
      }));
      this.selectedGroupId = this.groups.length > 0 ? this.groups[0].id : null;
      this.syncAiAcceptButton();
      this.syncActionCards();
    }
  }

  get selectedGroup(): SmartImportPageGroup | null {
    return this.groups.find((g) => g.id === this.selectedGroupId) ?? null;
  }

  get fileUrl(): string | null {
    return this.analysisResult?.fileUrl ?? null;
  }

  get previewPageNumber(): number {
    return this.selectedGroup?.pageNumbers?.[this.previewPageIndex] ?? 1;
  }

  get isPreviewFirstPage(): boolean {
    return this.previewPageIndex <= 0;
  }

  get isPreviewLastPage(): boolean {
    return !this.selectedGroup || this.previewPageIndex >= this.selectedGroup.pageNumbers.length - 1;
  }

  prevPreviewPage(): void {
    if (!this.isPreviewFirstPage) {
      this.previewPageIndex--;
    }
  }

  nextPreviewPage(): void {
    if (!this.isPreviewLastPage) {
      this.previewPageIndex++;
    }
  }

  // Once a group has an action, its rail-row radio dot is replaced by a checkmark —
  // signals "decided" independent of which row is currently selected/being viewed.
  isClassified(group: SmartImportPageGroup): boolean {
    return group.action !== null && group.action !== undefined;
  }

  selectGroup(groupId: number): void {
    this.selectedGroupId = groupId;
    this.previewPageIndex = 0;
    this.syncActionCards();
  }

  confidenceTier(group: SmartImportPageGroup): "high" | "medium" | "low" {
    if (group.confidence >= 95) return "high";
    if (group.confidence >= 80) return "medium";
    return "low";
  }

  // Maps a group's classification (or lack of one) to the app's existing `.--status`
  // pill modifiers (shared with `.grid-status`) — no dedicated "skip" color exists, so
  // it shares `--pending`'s amber, matching Skip's amber semantics elsewhere in the UI.
  statusClassFor(group: SmartImportPageGroup): string {
    if (group.action === this.Action.Import) return "--success";
    if (group.action === this.Action.Skip) return "--secondary";
    if (group.action === this.Action.NeverImport) return "--incomplete";
    return "--pending";
  }

  statusLabelKeyFor(group: SmartImportPageGroup): string {
    if (group.action === this.Action.Import) return "label_import";
    if (group.action === this.Action.Skip) return "skip";
    if (group.action === this.Action.NeverImport) return "smart_import_never_import";
    return "smart_import_pending_review";
  }

  confidenceStatusClass(group: SmartImportPageGroup): string {
    const tier = this.confidenceTier(group);
    if (tier === "high") return "--success";
    if (tier === "low") return "--incomplete";
    return "--pending";
  }

  confidenceIcon(group: SmartImportPageGroup): string {
    return this.confidenceTier(group) === "high" ? ICONS.CHECK_CIRCLE : ICONS.WARNING;
  }

  // Marks whichever of the 3 action buttons matches the AI's own recommendation — shown
  // independent of the 95% bulk-accept threshold, so even a lower-confidence group still
  // tells the FM what the AI would have picked. Hidden once the group is classified — the
  // suggestion is for an undecided group, not a label for a decision already made.
  isAiSuggested(group: SmartImportPageGroup, action: SmartImportPageGroupActionEnum): boolean {
    return group.aiRecommendation === action && !this.isClassified(group);
  }

  get reviewedCount(): number {
    return this.groups.filter((g) => g.action !== null && g.action !== undefined).length;
  }

  get totalGroups(): number {
    return this.groups.length;
  }

  private pagesFor(action: SmartImportPageGroupActionEnum): number {
    return this.groups.filter((g) => g.action === action).reduce((sum, g) => sum + g.pageCount, 0);
  }

  get importingPages(): number {
    return this.pagesFor(this.Action.Import);
  }

  get skippingPages(): number {
    return this.pagesFor(this.Action.Skip);
  }

  get neverImportPages(): number {
    return this.pagesFor(this.Action.NeverImport);
  }

  get eligibleForBulkAccept(): SmartImportPageGroup[] {
    return this.groups.filter(
      (g) =>
        (g.action === null || g.action === undefined) &&
        g.aiRecommendation !== this.Action.Unknown &&
        g.confidence >= 95
    );
  }

  get allReviewed(): boolean {
    return this.totalGroups > 0 && this.reviewedCount === this.totalGroups;
  }

  // How many groups are still unclassified and NOT covered by the bulk-accept CTA —
  // shown in the confirm modal's "will remain in Pending Review" notice.
  get remainingAfterAcceptCount(): number {
    return this.totalGroups - this.reviewedCount - this.eligibleForBulkAccept.length;
  }


  // Stand-in for the artifact's hand-authored `flagged` demo field: an unrecognised
  // layout (no AI recommendation at all) is the "this needs a closer look" case.
  isFlagged(group: SmartImportPageGroup): boolean {
    return group.aiRecommendation === this.Action.Unknown;
  }

  // ngx-extended-pdf-viewer's own ngOnDestroy defers part of its teardown via an
  // internal setTimeout(0) — mounting the next instance on the very next tick races
  // that still-in-flight cleanup, which then tears down the new instance's global
  // PDFViewerApplication instead of its own. A short real-time buffer (well beyond a
  // single macrotask) lets the old instance's teardown fully finish first.
  private static readonly VIEWER_HANDOFF_DELAY_MS = 200;

  openReader(): void {
    this.miniPreviewVisible = false;
    this.readerPageIndex = this.previewPageIndex;
    setTimeout(() => (this.readerOpen = true), SmartImportWizardComponent.VIEWER_HANDOFF_DELAY_MS);
  }

  closeReader(): void {
    this.readerOpen = false;
    setTimeout(() => {
      this.previewLoading = true;
      this.miniPreviewVisible = true;
    }, SmartImportWizardComponent.VIEWER_HANDOFF_DELAY_MS);
  }

  onPreviewLoadingStarts(): void {
    this.previewLoading = true;
  }

  onPreviewLoaded(): void {
    this.previewLoading = false;
  }

  // Gives the FM a moment to see the checkmark/status land on the row they just
  // classified before the rail jumps to the next unclassified group.
  private static readonly AUTO_ADVANCE_DELAY_MS = 800;

  classify(group: SmartImportPageGroup, action: SmartImportPageGroupActionEnum): void {
    group.action = action;
    group.rememberRule = group.rememberRule ?? true;
    // Classifying a group removes it from the eligible set, so the CTA's count moves.
    this.syncAiAcceptButton();
    // Reflects the just-made selection on the current group immediately; autoAdvance
    // (and the resync it needs) is deferred below.
    this.syncActionCards();
    setTimeout(() => {
      this.autoAdvance();
      // After autoAdvance, so the radio reflects whichever group is now selected.
      this.syncActionCards();
    }, SmartImportWizardComponent.AUTO_ADVANCE_DELAY_MS);
  }

  toggleRemember(group: SmartImportPageGroup): void {
    group.rememberRule = !(group.rememberRule ?? true);
  }

  private autoAdvance(): void {
    const next = this.groups.find((g) => g.action === null || g.action === undefined);
    if (next) {
      this.selectedGroupId = next.id;
    }
  }

  showAcceptSuggestionsModal = false;

  // The CTA opens a confirmation modal listing every group it's about to touch, rather
  // than applying immediately — the FM reviews the batch before it's committed.
  acceptAllHighConfidence(): void {
    if (this.eligibleForBulkAccept.length === 0) {
      return;
    }
    this.showAcceptSuggestionsModal = true;
  }

  cancelAcceptSuggestions(): void {
    this.showAcceptSuggestionsModal = false;
  }

  // Applies the AI recommendation to every group that was eligible when the modal
  // opened — matches the design artifact's `acceptAllAI` exactly.
  confirmAcceptSuggestions(): void {
    const eligible = this.eligibleForBulkAccept;
    this.showAcceptSuggestionsModal = false;
    if (eligible.length === 0) {
      return;
    }

    for (const group of eligible) {
      group.action = group.aiRecommendation;
      group.rememberRule = true;
    }
    // Everything eligible is now classified, so the CTA drops to its disabled empty state.
    this.syncAiAcceptButton();

    // Advance to the first group that still genuinely needs individual review
    // (unknown recommendation, or below the 95% gate) — matches the artifact's
    // `acceptAllAI` exactly; bulk-accept never touches these, so if none remain
    // every group is done.
    const next = this.groups.find(
      (g) => (g.action === null || g.action === undefined) && (g.aiRecommendation === this.Action.Unknown || g.confidence < 95)
    );
    if (next) {
      this.selectedGroupId = next.id;
    }
    this.syncActionCards();
  }

  cancel(): void {
    this.cancelled.emit();
  }

  submitImportAndScan(): void {
    if (!this.allReviewed || this.submitting) {
      return;
    }

    const importedTransactionCount = this.groups
      .filter((g) => g.action === this.Action.Import)
      .reduce((sum, g) => sum + g.transactions.length, 0);

    const request: ClassifySmartImportStatementRequest = {
      statementId: this.analysisResult.statementId,
      classifications: this.groups.map((g) => ({
        groupId: g.id,
        action: g.action as SmartImportPageGroupActionEnum,
        remember: !!g.rememberRule,
      })),
      amountCorrections: [],
    };

    const apiRequest = new ApiRequest<ClassifySmartImportStatementRequest>(
      "ClassifyCreditCardStatement",
      environment.applicationVersion,
      environment.requestId
    );
    apiRequest.requestInfo = request;

    this.submitClassification.emit({ request: apiRequest, transactionCount: importedTransactionCount });
  }
}
